import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import ExportVendorProfileExcel from "./Components/ExportVendorProfileExcel";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import {
  useLazyGetVendorProfileListQuery,
  useUpdateVendorProfileStatusMutation,
} from "store/services/vendorProfileApi";
import { toast } from "react-toastify";
import { BaseTabs } from "components/Shared/BaseTabs";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import UserSearchAutocomplete from "./Components/UserSearchAutocomplete";
import { useCustomParams } from "hooks/useCustomParams";

const VendorProfileListScreen = () => {
  const { isAccountAdmin, isSystemAdmin } = useAuth();

  const { params, appendParams, resetParams } = useCustomParams();

  const from = params.from;
  const to = params.to;
  const [searchParams] = useSearchParams();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const [addedUser, setAddedUser] = React.useState(null);
  const [onboardedBy, setOnboardedBy] = React.useState(null);
  const [referredBy, setReferredBy] = React.useState(null);
  const [relationshipManager, setRelationshipManager] = React.useState(null);

  const breadcrumbs = [
    {
      title: "Vendor-Profile",
    },
  ];

  const vendorProfileListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [updateVendorProfileStatus] = useUpdateVendorProfileStatusMutation();

  const handleStatusChange = (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`,
    );

    if (isConfirmed) {
      updateVendorProfileStatus({ id: row._id, status: newStatus })
        .unwrap()
        .then(() => {
          toast.success(`Vendor profile status updated to ${newStatus}`);
        })
        .catch((error) => {
          const errorMessage = error?.data?.message || "Something went wrong!";
          toast.error(errorMessage);
        });
    }
  };

  const columns = [
    {
      name: "Shop Name",
      selector: (row) => row?.shopName,
      cell: (row) => (
        <Link to={`/app/crm/vendor-profile/${row._id}/detail`}>
          <div title={row?.shopName} className="line-clamp-2">
            {row?.shopName ? (
              <HighlightedText text={row?.shopName} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </div>
        </Link>
      ),
      minWidth: "150px",
    },
    {
      name: "Phone",
      selector: (row) => row?.phone,
      cell: (row) => (
        <div title={row?.phone} className="line-clamp-2">
          {row?.phone ? (
            <HighlightedText text={row?.phone} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Owner Name",
      selector: (row) => row?.ownerName,
      cell: (row) => (
        <div title={row?.ownerName} className="line-clamp-2">
          {row?.ownerName ? (
            <HighlightedText text={row?.ownerName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Onboarded Date",
      selector: (row) =>
        <AppTableDateTime date={row?.createdAt} dateOnly /> ?? "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vendor-profile/${row._id}/detail`}>
            <Tooltip title={"View Detail"} arrow>
              <IconButton size="small" aria-label="View Detail">
                <InfoOutlined color="primary" />
              </IconButton>
            </Tooltip>
          </Link>
          {(isSystemAdmin || isAccountAdmin) && (
            <Link to={`/app/crm/vendor-profile/${row._id}/edit`}>
              <Tooltip title={"Edit"} arrow>
                <IconButton size="small" aria-label="Edit">
                  <ModeEditOutlineTwoToneIcon />
                </IconButton>
              </Tooltip>
            </Link>
          )}
          {(isSystemAdmin || isAccountAdmin) && (
            <Switch
              checked={row.status === "Active"}
              onChange={() => handleStatusChange(row)}
              color="primary"
              inputProps={{ "aria-label": "Status Toggle" }}
            />
          )}
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchVendorProfiles,
    {
      data: vendorProfileList,
      isLoading: loadingVendorProfiles,
      isFetching: fetchingVendorProfiles,
      error,
    },
  ] = useLazyGetVendorProfileListQuery();

  // Fetch vendor profiles
  React.useEffect(() => {
    fetchVendorProfiles({
      status: activeTab,
      keywords: keyword,
      page: currentPage,
      limit: perPage,
      ...(addedUser?._id && { addedUser: addedUser._id }),
      ...(onboardedBy?._id && { onboardedBy: onboardedBy._id }),
      ...(referredBy?._id && { referredBy: referredBy._id }),
      ...(relationshipManager?._id && {
        relationshipManager: relationshipManager._id,
      }),
      from,
      to,
    });
  }, [
    activeTab,
    fetchVendorProfiles,
    keyword,
    currentPage,
    perPage,
    addedUser,
    onboardedBy,
    referredBy,
    relationshipManager,
    from,
    to,
  ]);

  const activeFilters = [
    addedUser && {
      key: "addedUser",
      label: `Added By: ${addedUser.name}`,
      onClear: () => setAddedUser(null),
    },
    onboardedBy && {
      key: "onboardedBy",
      label: `Onboarded By: ${onboardedBy.name}`,
      onClear: () => setOnboardedBy(null),
    },
    referredBy && {
      key: "referredBy",
      label: `Referred By: ${referredBy.name}`,
      onClear: () => setReferredBy(null),
    },
    relationshipManager && {
      key: "relationshipManager",
      label: `Relationship Manager: ${relationshipManager.name}`,
      onClear: () => setRelationshipManager(null),
    },
    (from || to) && {
      key: "dateRange",
      label: `Date Range: ${from || "…"} - ${to || "…"}`,
      onClear: () => appendParams({ from: "", to: "" }),
    },
  ].filter(Boolean);

  useAppMeta(`${activeTab} Vendor Profile`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={vendorProfileListTabs}
          defaultValue={activeTab}
          actions={
            <Box display="flex" gap={1}>
              <ExportVendorProfileExcel status={activeTab} />
            </Box>
          }
        />
         <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          sx={{ rowGap: 0.5 }}
        >
          {activeFilters.length > 0 && (
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ flex: "1 1 260px", px: { xs: 2, sm: 4 }, py: 1 }}
            >
              <Box
                component="span"
                sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}
              >
                Active Filters:
              </Box>
              {activeFilters.map((filter) => (
                <Chip
                  key={filter.key}
                  label={filter.label}
                  onDelete={filter.onClear}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: "6px" }}
                />
              ))}
            </Box>
          )}
          <Box sx={{ flex: "0 0 auto", ml: "auto" }}>
            <TableFilter
              searchLabel="Owner Name / Shop Name / Phone"
              initialFilters={0}
              onClear={() => {
                resetParams();
                setAddedUser(null);
                setOnboardedBy(null);
                setReferredBy(null);
                setRelationshipManager(null);
              }}
            >
          <UserSearchAutocomplete
            label="Added By"
            value={addedUser}
            onChange={setAddedUser}
          />
          <UserSearchAutocomplete
            label="Onboarded By"
            value={onboardedBy}
            onChange={setOnboardedBy}
          />
          <UserSearchAutocomplete
            label="Referred By"
            value={referredBy}
            onChange={setReferredBy}
          />
          <UserSearchAutocomplete
            label="Relationship Manager"
            value={relationshipManager}
            onChange={setRelationshipManager}
          />
          <TextField
            fullWidth
            label="From Date"
            type="date"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            value={from || ""}
            onChange={(e) => {
              appendParams({ from: e.target.value });
            }}
          />

          <TextField
            fullWidth
            label="To Date"
            type="date"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            value={to || ""}
            onChange={(e) => {
              appendParams({ to: e.target.value });
            }}
          />
        </TableFilter>
          </Box>
        </Box>
        <AppTable
          columns={columns}
          data={vendorProfileList?.data}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loadingVendorProfiles || fetchingVendorProfiles}
          paginationTotalRows={vendorProfileList?.totalItems ?? 0}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VendorProfileListScreen;

const PageActions = () => {
  const { isAccountAdmin, isSystemAdmin } = useAuth();
  return (
    <div
      style={{
        display: "flex",
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      {(isAccountAdmin || isSystemAdmin) && (
        <Link to="/app/crm/vendor-profile/create">
          <Button startIcon={<AddIcon />} variant="contained">
            New Vendor Profile
          </Button>
        </Link>
      )}
    </div>
  );
};
